import { convertWhiteToTransparent } from "$/util/image_converter";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Slider,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { CirclePicker } from "react-color";
import ImageCanvasSimple from "./ImageCanvasSimple";

function applyAgingEffect(ctx, age) {
  const imageData = ctx.getImageData(0, 0, ctx.canvas.width, ctx.canvas.height);
  const data = imageData.data;

  // Adjust based on age (0-100)
  const fadeFactor = 1 - (age / 100) * 0.5; // Up to 50% fade
  const spreadFactor = 1 + (age / 100) * 0.2; // Up to 20% spread
  const blurFactor = Math.floor((age / 100) * 8); // Up to 4px blur

  // Create a temporary canvas for the spread effect
  const tempCanvas = document.createElement("canvas");
  tempCanvas.width = ctx.canvas.width;
  tempCanvas.height = ctx.canvas.height;
  const tempCtx = tempCanvas.getContext("2d");

  if (tempCtx) {
    // Draw the original image to temp canvas
    tempCtx.putImageData(imageData, 0, 0);

    // Apply spread effect
    if (spreadFactor > 1) {
      const spreadRadius = Math.floor((spreadFactor - 1) * 2); // Convert spread factor to pixels
      const spreadCanvas = document.createElement("canvas");
      spreadCanvas.width = ctx.canvas.width;
      spreadCanvas.height = ctx.canvas.height;
      const spreadCtx = spreadCanvas.getContext("2d");

      if (spreadCtx) {
        // Draw the image slightly larger to create spread effect
        spreadCtx.drawImage(
          tempCanvas,
          -spreadRadius,
          -spreadRadius,
          ctx.canvas.width + spreadRadius * 2,
          ctx.canvas.height + spreadRadius * 2,
        );

        // Clear the original canvas
        ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);

        // Draw the spread image back
        ctx.drawImage(spreadCanvas, 0, 0);

        // Get the new image data after spread
        const spreadData = ctx.getImageData(
          0,
          0,
          ctx.canvas.width,
          ctx.canvas.height,
        ).data;

        // Apply fade effect to the spread image
        for (let i = 0; i < spreadData.length; i += 4) {
          if (spreadData[i + 3] > 0) {
            // For black areas, we need to increase the value instead of decreasing
            if (
              spreadData[i] < 50 &&
              spreadData[i + 1] < 50 &&
              spreadData[i + 2] < 50
            ) {
              // For black areas, we'll lighten them by adding to the RGB values
              const lightenAmount = Math.floor((1 - fadeFactor) * 100);
              spreadData[i] = Math.min(255, spreadData[i] + lightenAmount);
              spreadData[i + 1] = Math.min(255, spreadData[i + 1] + lightenAmount);
              spreadData[i + 2] = Math.min(255, spreadData[i + 2] + lightenAmount);
            } else {
              // For non-black areas, apply normal fade
              spreadData[i] = Math.floor(spreadData[i] * fadeFactor);
              spreadData[i + 1] = Math.floor(spreadData[i + 1] * fadeFactor);
              spreadData[i + 2] = Math.floor(spreadData[i + 2] * fadeFactor);
            }
          }
        }

        ctx.putImageData(
          new ImageData(spreadData, ctx.canvas.width, ctx.canvas.height),
          0,
          0,
        );
      }
    } else {
      // If no spread, just apply fade effect
      for (let i = 0; i < data.length; i += 4) {
        if (data[i + 3] > 0) {
          // For black areas, we need to increase the value instead of decreasing
          if (data[i] < 50 && data[i + 1] < 50 && data[i + 2] < 50) {
            // For black areas, we'll lighten them by adding to the RGB values
            const lightenAmount = Math.floor((1 - fadeFactor) * 100);
            data[i] = Math.min(255, data[i] + lightenAmount);
            data[i + 1] = Math.min(255, data[i + 1] + lightenAmount);
            data[i + 2] = Math.min(255, data[i + 2] + lightenAmount);
          } else {
            // For non-black areas, apply normal fade
            data[i] = Math.floor(data[i] * fadeFactor);
            data[i + 1] = Math.floor(data[i + 1] * fadeFactor);
            data[i + 2] = Math.floor(data[i + 2] * fadeFactor);
          }
        }
      }
      ctx.putImageData(imageData, 0, 0);
    }

    // Apply blur effect for older tattoos
    if (blurFactor > 0) {
      const blurCanvas = document.createElement("canvas");
      blurCanvas.width = ctx.canvas.width;
      blurCanvas.height = ctx.canvas.height;
      const blurCtx = blurCanvas.getContext("2d");
      if (blurCtx) {
        blurCtx.filter = `blur(${blurFactor}px)`;
        blurCtx.drawImage(ctx.canvas, 0, 0);
        ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
        ctx.drawImage(blurCanvas, 0, 0);
      }
    }
  }
}

function OriginalImage({ imageUrl, width, height, age = 0 }) {
  const [imageDataUrl, setImageDataUrl] = useState(null);

  useEffect(() => {
    const img = new Image();
    img.src = imageUrl;
    img.crossOrigin = "Anonymous";

    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");

      if (ctx) {
        // Draw the image
        ctx.drawImage(img, 0, 0, width, height);

        convertWhiteToTransparent(ctx, 0, 250);

        // Apply aging effect if age > 0
        if (age > 0) {
          applyAgingEffect(ctx, age);
        }

        setImageDataUrl(canvas.toDataURL());
      }
    };
  }, [imageUrl, width, height, age]);

  return (
    <Box
      sx={{
        width: "100%",
        aspectRatio: width / height,
        display: "flex",
        maxHeight: "650px",
      }}
    >
      {imageDataUrl ? (
        <img
          src={imageDataUrl}
          alt="Original tattoo design"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
          }}
        />
      ) : (
        <Box sx={{ width: "100%", height: "100%" }} />
      )}
    </Box>
  );
}

function createSkinTexturePattern(color, size = 400) {
  // Create a canvas for the pattern
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");

  if (!ctx) return null;

  // Fill with base skin tone
  ctx.fillStyle = color;
  ctx.fillRect(0, 0, size, size);

  // Parse the color to determine if it's dark or light
  const r = parseInt(color.slice(1, 3), 16);
  const g = parseInt(color.slice(3, 5), 16);
  const b = parseInt(color.slice(5, 7), 16);
  const brightness = (r + g + b) / (3 * 255); // 0-1 scale

  // Adjust contrast based on skin tone brightness
  const darkOpacity = brightness < 0.5 ? 0.03 : 0.05;

  // First pass: Apply fine grain texture
  const grainCount = 2000;
  ctx.fillStyle = `rgba(0, 0, 0, ${darkOpacity})`;

  for (let i = 0; i < grainCount; i++) {
    const x = Math.random() * size;
    const y = Math.random() * size;
    const radius = 0.4 + Math.random();

    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fill();
  }

  // Second pass: Apply medium-sized variations for a more natural look
  const variationCount = 300;

  for (let i = 0; i < variationCount; i++) {
    const x = Math.random() * size;
    const y = Math.random() * size;
    const radius = 3 + Math.random() * 8;

    // No gradient needed as lightOpacity is zero
  }

  // Third pass: Add some linear structures mimicking skin patterns
  ctx.strokeStyle = `rgba(0, 0, 0, ${darkOpacity / 2})`;

  // Regular dark lines for all skin tones
  ctx.lineWidth = 0.5;

  for (let i = 0; i < 50; i++) {
    const x1 = Math.random() * size;
    const y1 = Math.random() * size;
    const length = 10 + Math.random() * 20;
    const angle = Math.random() * Math.PI * 2;

    const x2 = x1 + Math.cos(angle) * length;
    const y2 = y1 + Math.sin(angle) * length;

    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();
  }

  // Apply subtle noise
  const imageData = ctx.getImageData(0, 0, size, size);
  const data = imageData.data;

  for (let i = 0; i < data.length; i += 4) {
    const noise = Math.random() * 6 - 3;
    data[i] = Math.max(0, Math.min(255, data[i] + noise));
    data[i + 1] = Math.max(0, Math.min(255, data[i + 1] + noise));
    data[i + 2] = Math.max(0, Math.min(255, data[i + 2] + noise));
  }

  ctx.putImageData(imageData, 0, 0);

  return canvas.toDataURL();
}

function VisualizationModal({ open, onClose, imageUrl, item }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [bgColor, setBgColor] = useState("#e0bb95");
  const [shadingPattern, setShadingPattern] = useState("original");
  const [threshold, setThreshold] = useState(0.25);
  const [age, setAge] = useState(0);
  const [skinTextureUrl, setSkinTextureUrl] = useState(null);

  const width =
    (item && item.params && item.params.width ? item.params.width : 512) * 2;
  const height =
    (item && item.params && item.params.height ? item.params.height : 512) * 2;

  // Generate skin texture when background color changes
  useEffect(() => {
    // Skip texture for white background (#f4f4f4)
    if (bgColor === "#f4f4f4") {
      setSkinTextureUrl(null);
      return;
    }

    const texture = createSkinTexturePattern(bgColor);
    setSkinTextureUrl(texture);
  }, [bgColor]);

  const handleShadingPatternChange = (event, newPattern) => {
    if (newPattern !== null) {
      setShadingPattern(newPattern);
    }
  };

  const handleThresholdChange = (event, newThreshold) => {
    if (newThreshold !== null) {
      setThreshold(newThreshold);
    }
  };

  const handleAgeChange = (event, newAge) => {
    setAge(newAge);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Visualize Your Tattoo</Typography>
          <IconButton onClick={onClose} aria-label="close" size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                backgroundColor: bgColor,
                backgroundImage:
                  bgColor !== "#f4f4f4" && skinTextureUrl
                    ? `url(${skinTextureUrl})`
                    : "none",
                backgroundSize: "cover",
                borderRadius: 2,
                mb: 2,
              }}
            >
              {shadingPattern === "original" ? (
                <OriginalImage
                  imageUrl={imageUrl}
                  width={width}
                  height={height}
                  age={age}
                />
              ) : (
                <ImageCanvasSimple
                  key="processed"
                  imageUrl={imageUrl}
                  width={width}
                  height={height}
                  blurRadius={0.5}
                  shadingPattern={shadingPattern}
                  threshold={threshold}
                />
              )}
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Stack spacing={3}>
              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  Tattoo Style
                </Typography>
                <ToggleButtonGroup
                  value={shadingPattern}
                  exclusive
                  onChange={handleShadingPatternChange}
                  fullWidth
                >
                  <ToggleButton value="original" aria-label="original">
                    Original
                  </ToggleButton>
                  <ToggleButton value="simple" aria-label="stencil">
                    Stencil
                  </ToggleButton>
                  <ToggleButton value="dotwork" aria-label="dotwork">
                    Dotwork
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>

              {shadingPattern !== "original" && (
                <Box>
                  <Typography variant="subtitle1" gutterBottom>
                    Shading Level
                  </Typography>
                  <ToggleButtonGroup
                    value={threshold}
                    exclusive
                    onChange={handleThresholdChange}
                    fullWidth
                  >
                    <ToggleButton value={0.35} aria-label="light">
                      Light
                    </ToggleButton>
                    <ToggleButton value={0.25} aria-label="medium">
                      Medium
                    </ToggleButton>
                    <ToggleButton value={0.2} aria-label="heavy">
                      Heavy
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              )}

              {shadingPattern === "original" && (
                <Box>
                  <Typography variant="subtitle1" gutterBottom>
                    Age Simulation
                  </Typography>
                  <Slider
                    value={age}
                    onChange={handleAgeChange}
                    min={0}
                    max={100}
                    step={25}
                    marks={[
                      { value: 0, label: "New" },
                      { value: 25, label: "1Y" },
                      { value: 50, label: "5Y" },
                      { value: 75, label: "10Y" },
                      { value: 100, label: "20Y" },
                    ]}
                    valueLabelDisplay="off"
                    sx={{ mx: 1, width: "calc(100% - 16px)" }}
                  />
                </Box>
              )}

              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  Skin Tone
                </Typography>
                <CirclePicker
                  disableAlpha
                  circleSize={24}
                  circleSpacing={8}
                  width="100%"
                  color={bgColor}
                  colors={[
                    "#f4f4f4",
                    "#FADCBC",
                    "#E0BB95",
                    "#BF8F68",
                    "#9B633D",
                    "#594539",
                  ]}
                  onChange={(val) => {
                    setBgColor(val.hex);
                  }}
                />
              </Box>
            </Stack>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
}

export default VisualizationModal;
